import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Autocomplete, Box, Container, IconButton, InputAdornment, Pagination, Skeleton, Stack, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Close';
import PageHeader from 'components/common/PageHeader/PageHeader';
import CustomText from 'components/common/CustomText/CustomText';
import useGlobalStyles from 'styles/globalStyles';
import { colors } from 'styles/colors';
import { snackbarService } from 'services/snackbarService';
import {
  useDeleteProductSuggestion,
  useDeleteSuggestionImage,
  useReextractRawProductOcr,
  useReextractRawProductInfo,
  useReextractRawProductBrand,
  useGetBrands,
  useUpdateRawProductBrand,
  useReextractRawProductContents,
  useReextractRawProductUsages,
  useUpdateRawProductIngredients,
  useReextractRawProductWarnings,
  useRankRawProductIngredients,
  useRerunRawProductPipeline,
  useRunMissingRawProductPasses,
  useRerunRawProductStep,
  useRedescribeRawProduct,
  useUpdateRawProductDescription,
  useUpdateRawProductUsages,
  useUpdateRawProductContents,
  useUpdateRawProductWarnings,
  useReextractRawProductAttributes,
  useUpdateRawProductAttributes,
  useGetLeafCategories,
  useGetRawProductsForReview,
  usePromoteRawProduct,
  useResolveRawProductCategory,
  useSetRawProductAvatar,
  useUpdateRawProductIdentity,
  useUpdateRawProductInfo,
} from 'hooks/useRawProductCategoryReview';
import { useCountryCodes, useProductIdentityValues } from 'hooks/useLookups';
import { CountryOption, ProductIdentityValues } from 'services/lookupsService';
import {
  RawProductIdentityPatch,
  RawProductInfoPatch,
  RawProductReadiness,
  RawProductStep,
  RedescribeRawProductResult,
  RawProductDescriptionPatch,
  RawProductUsageEdit,
  RawProductContentsPatch,
  RawProductWarnings,
  RawProductIngredient,
  RawProductAttributes,
  ReextractRawProductContentsResult,
  ReextractRawProductUsagesResult,
  ReextractRawProductIngredientsResult,
  ReextractRawProductWarningsResult,
  ReextractRawProductAttributesResult,
  RankRawProductIngredientsResult,
  UpdateRawProductIdentityResult,
  UpdateRawProductInfoResult,
  ReextractRawProductBrandResult,
  RawProductBrandPatch,
  LeafCategoryOption,
  RawProductReviewItem,
  getRawProductsForReview,
} from 'services/rawProductService';
import RawProductReviewCard from './RawProductReviewCard';
import { IdentityFieldName } from './IdentityFieldModal';
import { InfoFieldName } from './InfoFieldModal';

const PAGE_SIZE = 5;

export default function RawProductReviewPage() {
  const globalStyles = useGlobalStyles();
  // The same page, asked for one row instead of the queue. A raw product reached
  // from a category carousel is read and corrected with exactly the controls the
  // queue offers, so it is this page with the queue's own furniture - the search
  // box and the pager - left off rather than a second copy of the card.
  const { rawProductId } = useParams<{ rawProductId?: string }>();
  const isSingle = !!rawProductId;
  const [page, setPage] = useState(1);
  // What is in the box, and what has actually been asked for. Kept apart so the
  // queue is not refetched on every keystroke of a thirteen-digit number: the
  // search runs when the reviewer says so.
  const [barcodeInput, setBarcodeInput] = useState('');
  // What is typed in the jump box, separate from the page actually being shown,
  // for the same reason the barcode is: a half-typed page number is not a page.
  const [pageInput, setPageInput] = useState('');
  const [barcode, setBarcode] = useState('');
  // Which leaf the queue is narrowed to, null for all of it. Kept beside the
  // barcode because they are the same kind of thing: a way to stop paging.
  const [leafCategory, setLeafCategory] = useState<LeafCategoryOption | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [promotingId, setPromotingId] = useState<string | null>(null);
  const [reextractingId, setReextractingId] = useState<string | null>(null);
  const [deletingSuggestionId, setDeletingSuggestionId] = useState<string | null>(null);
  const [redescribingId, setRedescribingId] = useState<string | null>(null);
  const [savingIdentityId, setSavingIdentityId] = useState<string | null>(null);
  const [savingInfoId, setSavingInfoId] = useState<string | null>(null);
  const [reextractingInfoId, setReextractingInfoId] = useState<string | null>(null);
  const [reextractingBrandId, setReextractingBrandId] = useState<string | null>(null);
  const [editingBrandId, setEditingBrandId] = useState<string | null>(null);
  const [savingBrandId, setSavingBrandId] = useState<string | null>(null);
  const [reextractingContentsId, setReextractingContentsId] = useState<string | null>(null);
  const [reextractingUsagesId, setReextractingUsagesId] = useState<string | null>(null);
  const [savingIngredientsId, setSavingIngredientsId] = useState<string | null>(null);
  const [reextractingWarningsId, setReextractingWarningsId] = useState<string | null>(null);
  const [rankingIngredientsId, setRankingIngredientsId] = useState<string | null>(null);
  const [rerunningPipelineId, setRerunningPipelineId] = useState<string | null>(null);
  // A set, not one id: several cards can be reading at once, and each spinner
  // has to answer for its own card rather than for whichever was pressed last.
  const [runningMissingIds, setRunningMissingIds] = useState<Set<string>>(new Set());
  const [settingAvatarId, setSettingAvatarId] = useState<string | null>(null);
  // Which pass is running, and on which card. One at a time across the page:
  // the passes write to the same row and the later ones read what the earlier
  // ones wrote, so two in flight would race over the same columns.
  const [rerunningStep, setRerunningStep] = useState<{
    rawProductId: string;
    step: RawProductStep;
  } | null>(null);

  // Which editor is open, across the whole page: the modals are single dialogs,
  // and two open at once would be two answers to a question asked of one card.
  const [editingField, setEditingField] = useState<{
    rawProductId: string;
    field: IdentityFieldName;
  } | null>(null);
  const [editingInfoField, setEditingInfoField] = useState<{
    rawProductId: string;
    field: InfoFieldName;
  } | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  // The description is edited whole rather than a field at a time, so the card
  // being edited is all this has to hold.
  const [editingDescriptionId, setEditingDescriptionId] = useState<string | null>(null);
  const [savingDescriptionId, setSavingDescriptionId] = useState<string | null>(null);
  const [savingUsagesId, setSavingUsagesId] = useState<string | null>(null);
  const [savingContentsId, setSavingContentsId] = useState<string | null>(null);
  const [savingWarningsId, setSavingWarningsId] = useState<string | null>(null);
  const [reextractingAttributesId, setReextractingAttributesId] = useState<string | null>(null);
  const [savingAttributesId, setSavingAttributesId] = useState<string | null>(null);
  const [attributes, setAttributes] = useState<
    Record<string, ReextractRawProductAttributesResult>
  >({});

  // What a save stored, held here rather than refetched into the list: a refetch
  // would reorder the queue under a reviewer who is mid-read, and the answer
  // already carries what the row now holds.
  const [descriptions, setDescriptions] = useState<
    Record<string, RedescribeRawProductResult>
  >({});
  const [contents, setContents] = useState<
    Record<string, ReextractRawProductContentsResult>
  >({});
  const [usages, setUsages] = useState<Record<string, ReextractRawProductUsagesResult>>({});
  const [ingredients, setIngredients] = useState<
    Record<string, ReextractRawProductIngredientsResult>
  >({});
  const [rankedIngredients, setRankedIngredients] = useState<
    Record<string, RankRawProductIngredientsResult>
  >({});
  const [warnings, setWarnings] = useState<
    Record<string, ReextractRawProductWarningsResult>
  >({});
  const [identities, setIdentities] = useState<Record<string, UpdateRawProductIdentityResult>>({});
  const [infos, setInfos] = useState<Record<string, UpdateRawProductInfoResult>>({});
  const [brands, setBrands] = useState<Record<string, ReextractRawProductBrandResult>>({});

  // What each row is waiting for as of the last write to it. One slot per row,
  // last writer wins: every answer that can change readiness carries the whole
  // picture as it stood when it was computed, so keeping one per kind of write
  // would only raise the question of which of them is newest.
  const [readiness, setReadiness] = useState<Record<string, RawProductReadiness>>({});
  // The front photo a reviewer picked, so the tick moves without refetching the
  // queue - a refetch would reorder it under someone mid-read.
  const [avatars, setAvatars] = useState<Record<string, string>>({});
  const recordReadiness = (result: RawProductReadiness & { rawProductId: string }) =>
    setReadiness((existing) => ({
      ...existing,
      [result.rawProductId]: { stage: result.stage, blockers: result.blockers },
    }));

  // One queue: every raw product that has not become one. Each card says what
  // it is waiting for, so splitting them into states would only hide rows behind
  // a tab nobody remembered to open.
  const { data, isLoading, refetch: refetchQueue } = useGetRawProductsForReview(
    isSingle
      ? { page: 1, limit: 1, rawProductId }
      : {
          page,
          limit: PAGE_SIZE,
          // Left off entirely when empty, so the key for the unfiltered queue stays
          // the one it has always been.
          ...(barcode ? { barcode } : {}),
          ...(leafCategory ? { leafCategoryId: leafCategory.id } : {}),
        },
    // Not on focus: coming back to the tab asked the queue again and took the
    // card out from under whoever was reading it.
    { refetchOnWindowFocus: false },
  );

  // Cards the queue no longer returns but the reviewer is still on. Filing a row
  // under another category takes it out of a queue filtered by the old one, and
  // the card went with it before the pick could be looked at. Held where it
  // stood until the queue itself changes - a filter, a search, a page.
  const [heldItems, setHeldItems] = useState<
    Record<string, { item: RawProductReviewItem; index: number }>
  >({});
  const pendingHold = useRef<{ rawProductId: string; index: number } | null>(null);

  const holdItem = async (heldId: string, index: number) => {
    const held = await getRawProductsForReview({ page: 1, limit: 1, rawProductId: heldId });
    const item = held.items[0];
    if (item) setHeldItems((existing) => ({ ...existing, [heldId]: { item, index } }));
  };

  useEffect(() => {
    setHeldItems({});
  }, [page, barcode, leafCategory?.id]);

  const items = useMemo(() => {
    const list = [...(data?.items ?? [])];
    Object.entries(heldItems)
      .sort((left, right) => left[1].index - right[1].index)
      .forEach(([heldId, held]) => {
        if (list.some((item) => item.rawProductId === heldId)) return;
        list.splice(Math.min(held.index, list.length), 0, held.item);
      });
    return list;
  }, [data, heldItems]);

  // The held cards are read again with the queue, which would otherwise leave
  // them showing what they held before the change that asked for the refetch.
  const refetch = () => {
    refetchQueue();
    Object.entries(heldItems).forEach(([heldId, held]) => holdItem(heldId, held.index));
  };

  // A search is a different queue, so it starts at its first page rather than
  // on whatever page the reviewer had reached in the old one.
  const runSearch = (next: string) => {
    setPage(1);
    setBarcode(next.trim());
  };

  // A category is a different queue too, so it starts at its own first page.
  const runCategoryFilter = (next: LeafCategoryOption | null) => {
    setPage(1);
    setLeafCategory(next);
  };
  const { data: leafCategories } = useGetLeafCategories();

  // Ordered by what is waiting there rather than by name: the filter exists to
  // find a category with rows to work through, and a name is what the search
  // box is for. Fewest first, because a category of three is finished in a
  // sitting and a category of three hundred is not; the ones with nothing
  // waiting go to the end, since there is nothing there to open. The card's own
  // picker keeps the alphabetical order it gets.
  const filterCategories = useMemo(
    () =>
      [...(leafCategories ?? [])].sort((a, b) => {
        const left = a.waiting ?? 0;
        const right = b.waiting ?? 0;
        if (!left !== !right) return left ? -1 : 1;
        return left - right || a.pathEN.localeCompare(b.pathEN);
      }),
    [leafCategories],
  );
  const { data: brandOptions, refetch: refetchBrands } = useGetBrands();
  const { data: identityValues } = useProductIdentityValues();
  const { data: countries } = useCountryCodes();

  // Refetched rather than patched in place: the pick feeds what the card can do
  // next, and the row's blockers are computed server-side.
  const { mutate: confirmCategory } = useResolveRawProductCategory(
    () => {
      setConfirmingId(null);
      setEditingCategoryId(null);
      snackbarService.showSnackbar('Category confirmed', 'success');
      // Held before the queue is asked again, so the card is already kept by the
      // time the answer comes back without it.
      const pending = pendingHold.current;
      pendingHold.current = null;
      if (pending) holdItem(pending.rawProductId, pending.index).finally(refetch);
      else refetch();
    },
    () => {
      setConfirmingId(null);
      pendingHold.current = null;
      snackbarService.showSnackbar('Could not confirm the category', 'error');
    },
  );

  const handleConfirmCategory = (rawProductId: string, leafCategoryId: string) => {
    pendingHold.current = {
      rawProductId,
      index: Math.max(0, items.findIndex((item) => item.rawProductId === rawProductId)),
    };
    setConfirmingId(rawProductId);
    confirmCategory({ rawProductId, leafCategoryId });
  };

  // A promoted row leaves the queue for good - it is a product now.
  // A promoted row leaves the queue on the next fetch, so the card is kept on
  // screen by remembering what it became: the reviewer who just pressed the
  // button wants to open the product from here, not to watch the card vanish.
  // Gone on reload, which is when the queue is asked again.
  const [promotedProductIds, setPromotedProductIds] = useState<Record<string, string>>({});

  const { mutate: promote } = usePromoteRawProduct(
    (result) => {
      setPromotingId(null);
      setPromotedProductIds((existing) => ({ ...existing, [result.rawProductId]: result.productId }));
      snackbarService.showSnackbar('Product created, unpublished', 'success');
    },
    (error: any) => {
      setPromotingId(null);
      snackbarService.showSnackbar(
        error?.response?.data?.message ?? 'Could not promote it',
        'error',
      );
    },
  );

  const handlePromote = (rawProductId: string) => {
    setPromotingId(rawProductId);
    promote(rawProductId);
  };

  // The card stays and reloads with the new label text. Every later pass reads
  // these same words, so a bad scan is worth re-reading before trusting anything
  // on the card - but that is the reviewer's call to make.
  const { mutate: reextractOcr } = useReextractRawProductOcr(
    () => {
      setReextractingId(null);
      snackbarService.showSnackbar('Label read again - the text below is new', 'success');
      refetch();
    },
    () => {
      setReextractingId(null);
      snackbarService.showSnackbar('Could not read the label again', 'error');
    },
  );

  const handleReextractOcr = (rawProductId: string) => {
    setReextractingId(rawProductId);
    reextractOcr(rawProductId);
  };

  const { mutate: redescribe } = useRedescribeRawProduct(
    (result) => {
      setRedescribingId(null);
      // The whole answer, not just the sentences: it also carries what the row
      // is waiting for now that it has them, which is what the card's chip,
      // button and missing-info list read.
      setDescriptions((existing) => ({ ...existing, [result.rawProductId]: result }));
      recordReadiness(result);
      snackbarService.showSnackbar(
        result.descriptionEn || result.descriptionAr
          ? 'Description written again'
          : 'The label and photos did not say enough to describe it',
        result.descriptionEn || result.descriptionAr ? 'success' : 'info',
      );
    },
    () => {
      setRedescribingId(null);
      snackbarService.showSnackbar('Could not write the description again', 'error');
    },
  );

  const handleRedescribe = (rawProductId: string) => {
    setRedescribingId(rawProductId);
    redescribe(rawProductId);
  };

  // Contents change nothing promotion waits for, so this one updates its card
  // and leaves the queue alone: a refetch would reorder the list under whoever
  // asked for the reading.
  const { mutate: reextractContents } = useReextractRawProductContents(
    (result) => {
      setReextractingContentsId(null);
      setContents((existing) => ({ ...existing, [result.rawProductId]: result }));
      snackbarService.showSnackbar(
        result.contents
          ? 'Read what is in the pack'
          : 'This pack holds nothing separate',
        result.contents ? 'success' : 'info',
      );
    },
    () => {
      setReextractingContentsId(null);
      snackbarService.showSnackbar('Could not read what is in the pack', 'error');
    },
  );

  // The ticked photos travel with the request. Nothing stores them: they say
  // which photos to read now, not something about the pack.
  const handleReextractContents = (rawProductId: string, mediaIds: string[]) => {
    setReextractingContentsId(rawProductId);
    reextractContents({ rawProductId, mediaIds });
  };

  // How the product is used. Nothing promotion waits for changes with it, so the
  // answer updates its own card and the queue is left alone.
  const { mutate: reextractUsages } = useReextractRawProductUsages(
    (result) => {
      setReextractingUsagesId(null);
      setUsages((existing) => ({ ...existing, [result.rawProductId]: result }));
      snackbarService.showSnackbar(
        result.usages?.length
          ? 'Written how this is used'
          : 'The label and photos did not say enough to write how this is used',
        result.usages?.length ? 'success' : 'info',
      );
    },
    () => {
      setReextractingUsagesId(null);
      snackbarService.showSnackbar('Could not write how this is used', 'error');
    },
  );

  const handleReextractUsages = (rawProductId: string, mediaIds: string[]) => {
    setReextractingUsagesId(rawProductId);
    reextractUsages({ rawProductId, mediaIds });
  };

  // The ingredients panel, as the reviewer confirmed it in the modal. Nothing
  // promotion waits for changes with it either, so the answer updates its own
  // card and the queue is left alone.
  const { mutate: updateIngredients } = useUpdateRawProductIngredients(
    (result) => {
      setSavingIngredientsId(null);
      setIngredients((existing) => ({ ...existing, [result.rawProductId]: result }));
      snackbarService.showSnackbar('Saved to the product record', 'success');
    },
    () => {
      setSavingIngredientsId(null);
      snackbarService.showSnackbar('Could not save it', 'error');
    },
  );

  const handleSaveIngredients = (rawProductId: string, next: RawProductIngredient[]) => {
    setSavingIngredientsId(rawProductId);
    updateIngredients({ rawProductId, ingredients: next });
  };

  // The pack's printed cautions. Nothing promotion waits for changes with them,
  // so the answer updates its own card and the queue is left alone. An empty
  // answer is the ordinary one - most packs print no warnings at all - so it is
  // reported as a fact rather than as a failure.
  const { mutate: reextractWarnings } = useReextractRawProductWarnings(
    (result) => {
      setReextractingWarningsId(null);
      setWarnings((existing) => ({ ...existing, [result.rawProductId]: result }));
      snackbarService.showSnackbar(
        result.warnings?.en?.length
          ? `Read ${result.warnings.en.length} warnings off the pack`
          : 'This pack prints no warnings',
        result.warnings?.en?.length ? 'success' : 'info',
      );
    },
    () => {
      setReextractingWarningsId(null);
      snackbarService.showSnackbar('Could not read the warnings', 'error');
    },
  );

  const handleReextractWarnings = (rawProductId: string) => {
    setReextractingWarningsId(rawProductId);
    reextractWarnings(rawProductId);
  };

  // The order over the stored panel. It refuses a row whose panel was never
  // read, and that refusal is worth showing as it comes back: it names the pass
  // the reviewer has to run first.
  const { mutate: rankIngredients } = useRankRawProductIngredients(
    (result) => {
      setRankingIngredientsId(null);
      setRankedIngredients((existing) => ({ ...existing, [result.rawProductId]: result }));
      snackbarService.showSnackbar(
        `Ordered ${result.rankedIngredients?.length ?? 0} ingredients`,
        'success',
      );
    },
    () => {
      setRankingIngredientsId(null);
      snackbarService.showSnackbar('Could not order the ingredients', 'error');
    },
  );

  const handleRankIngredients = (rawProductId: string) => {
    setRankingIngredientsId(rawProductId);
    rankIngredients(rawProductId);
  };

  const { mutate: updateIdentity } = useUpdateRawProductIdentity(
    (result) => {
      setSavingIdentityId(null);
      setEditingField(null);
      setIdentities((existing) => ({ ...existing, [result.rawProductId]: result }));
      recordReadiness(result);
      snackbarService.showSnackbar('Fields saved to the product', 'success');
    },
    () => {
      setSavingIdentityId(null);
      snackbarService.showSnackbar('Could not save the fields', 'error');
    },
  );

  const handleSaveIdentity = (rawProductId: string, patch: RawProductIdentityPatch) => {
    setSavingIdentityId(rawProductId);
    updateIdentity({ rawProductId, identity: patch });
  };

  // Saving a particular can complete a row, which changes which tab it belongs
  // in - so unlike an identity edit this one refetches.
  const { mutate: updateInfo } = useUpdateRawProductInfo(
    (result) => {
      setSavingInfoId(null);
      setEditingInfoField(null);
      setInfos((existing) => ({ ...existing, [result.rawProductId]: result }));
      recordReadiness(result);
      snackbarService.showSnackbar('Saved to the product record', 'success');
      refetch();
    },
    () => {
      setSavingInfoId(null);
      snackbarService.showSnackbar('Could not save it', 'error');
    },
  );

  // The same answer a re-read gives, so it lands in the same place: the card
  // shows a typed description exactly the way it shows a written one, and the
  // queue is left alone.
  const { mutate: updateDescription } = useUpdateRawProductDescription(
    (result) => {
      setSavingDescriptionId(null);
      setEditingDescriptionId(null);
      setDescriptions((existing) => ({ ...existing, [result.rawProductId]: result }));
      recordReadiness(result);
      snackbarService.showSnackbar('Saved to the product record', 'success');
    },
    () => {
      setSavingDescriptionId(null);
      snackbarService.showSnackbar('Could not save it', 'error');
    },
  );

  const handleSaveDescription = (
    rawProductId: string,
    patch: RawProductDescriptionPatch,
  ) => {
    setSavingDescriptionId(rawProductId);
    updateDescription({ rawProductId, description: patch });
  };

  // A re-read and a correction answer in the same shape and land in the same
  // place, so the card shows either the way it shows the other.
  const { mutate: reextractAttributes } = useReextractRawProductAttributes(
    (result) => {
      setReextractingAttributesId(null);
      setAttributes((existing) => ({ ...existing, [result.rawProductId]: result }));
      snackbarService.showSnackbar('Read what the pack claims again', 'success');
    },
    () => {
      setReextractingAttributesId(null);
      snackbarService.showSnackbar('Could not read what the pack claims', 'error');
    },
  );

  const handleReextractAttributes = (rawProductId: string, withDescription = false) => {
    setReextractingAttributesId(rawProductId);
    reextractAttributes({ rawProductId, withDescription });
  };

  const { mutate: updateAttributes } = useUpdateRawProductAttributes(
    (result) => {
      setSavingAttributesId(null);
      setAttributes((existing) => ({ ...existing, [result.rawProductId]: result }));
      snackbarService.showSnackbar('Saved to the product record', 'success');
    },
    () => {
      setSavingAttributesId(null);
      snackbarService.showSnackbar('Could not save it', 'error');
    },
  );

  const handleSaveAttributes = (rawProductId: string, next: RawProductAttributes) => {
    setSavingAttributesId(rawProductId);
    updateAttributes({ rawProductId, attributes: next });
  };

  // Lands where a re-read's answer lands, like the two below it.
  const { mutate: updateWarnings } = useUpdateRawProductWarnings(
    (result) => {
      setSavingWarningsId(null);
      setWarnings((existing) => ({ ...existing, [result.rawProductId]: result }));
      snackbarService.showSnackbar('Saved to the product record', 'success');
    },
    () => {
      setSavingWarningsId(null);
      snackbarService.showSnackbar('Could not save it', 'error');
    },
  );

  const handleSaveWarnings = (
    rawProductId: string,
    next: RawProductWarnings,
  ) => {
    setSavingWarningsId(rawProductId);
    updateWarnings({ rawProductId, warnings: next });
  };

  // Lands where a re-read's answer lands, like the procedures below it.
  const { mutate: updateContents } = useUpdateRawProductContents(
    (result) => {
      setSavingContentsId(null);
      setContents((existing) => ({ ...existing, [result.rawProductId]: result }));
      snackbarService.showSnackbar('Saved to the product record', 'success');
    },
    () => {
      setSavingContentsId(null);
      snackbarService.showSnackbar('Could not save it', 'error');
    },
  );

  const handleSaveContents = (rawProductId: string, patch: RawProductContentsPatch) => {
    setSavingContentsId(rawProductId);
    updateContents({ rawProductId, contents: patch });
  };

  // Lands where a re-read's answer lands, for the same reason: the card shows
  // corrected procedures the way it shows written ones, and the queue is not
  // reordered under whoever is reading it.
  const { mutate: updateUsages } = useUpdateRawProductUsages(
    (result) => {
      setSavingUsagesId(null);
      setUsages((existing) => ({ ...existing, [result.rawProductId]: result }));
      snackbarService.showSnackbar('Saved to the product record', 'success');
    },
    () => {
      setSavingUsagesId(null);
      snackbarService.showSnackbar('Could not save it', 'error');
    },
  );

  const handleSaveUsages = (rawProductId: string, usages: RawProductUsageEdit[]) => {
    setSavingUsagesId(rawProductId);
    updateUsages({ rawProductId, usages });
  };

  const handleSaveInfo = (rawProductId: string, patch: RawProductInfoPatch) => {
    setSavingInfoId(rawProductId);
    updateInfo({ rawProductId, info: patch });
  };

  const { mutate: reextractInfo } = useReextractRawProductInfo(
    (result) => {
      setReextractingInfoId(null);
      setInfos((existing) => ({ ...existing, [result.rawProductId]: result }));
      recordReadiness(result);
      snackbarService.showSnackbar('Read off the pack again', 'success');
      refetch();
    },
    () => {
      setReextractingInfoId(null);
      snackbarService.showSnackbar('Could not read the pack again', 'error');
    },
  );

  const handleReextractInfo = (rawProductId: string) => {
    setReextractingInfoId(rawProductId);
    reextractInfo(rawProductId);
  };

  const { mutate: reextractBrand } = useReextractRawProductBrand(
    (result) => {
      setReextractingBrandId(null);
      setBrands((existing) => ({ ...existing, [result.rawProductId]: result }));
      snackbarService.showSnackbar('Brand read off the pack again', 'success');
      // The reading can have created a brand, or added a spelling to one, so the
      // picker's list is fetched again rather than left as it was loaded.
      refetchBrands();
    },
    () => {
      setReextractingBrandId(null);
      snackbarService.showSnackbar('Could not read the brand again', 'error');
    },
  );

  const handleReextractBrand = (rawProductId: string) => {
    setReextractingBrandId(rawProductId);
    reextractBrand(rawProductId);
  };

  const { mutate: updateBrand } = useUpdateRawProductBrand(
    (result) => {
      setSavingBrandId(null);
      setEditingBrandId(null);
      setBrands((existing) => ({ ...existing, [result.rawProductId]: result }));
      snackbarService.showSnackbar('Brand saved', 'success');
      // A rename reaches every card under that brand, so the queue is reloaded
      // rather than only this card patched.
      refetchBrands();
      refetch();
    },
    () => {
      setSavingBrandId(null);
      snackbarService.showSnackbar('Could not save the brand', 'error');
    },
  );

  const handleSaveBrand = (rawProductId: string, patch: RawProductBrandPatch) => {
    setSavingBrandId(rawProductId);
    updateBrand({ rawProductId, patch });
  };

  // The whole run can change which tab this row belongs in - it settles the
  // category - so this one refetches rather than patching the card in place.
  const { mutate: rerunPipeline } = useRerunRawProductPipeline(
    (result) => {
      setRerunningPipelineId(null);
      const failed = result.steps.filter((step) => !step.success);
      snackbarService.showSnackbar(
        failed.length
          ? `Read again, except: ${failed.map((step) => step.step).join(', ')}`
          : 'Read again from the pack',
        failed.length ? 'info' : 'success',
      );
      // Ahead of the refetch, so the card stops asking for a tag or a category
      // the run just settled without waiting for the queue to come back.
      recordReadiness(result);
      refetch();
    },
    () => {
      setRerunningPipelineId(null);
      snackbarService.showSnackbar('Could not read the pack again', 'error');
    },
  );

  const handleRerunPipeline = (rawProductId: string) => {
    setRerunningPipelineId(rawProductId);
    rerunPipeline(rawProductId);
  };

  // Touches whichever sections were unread, so like the pipeline it refetches
  // rather than patching one block of the card.
  const stopRunningMissing = (rawProductId: string) =>
    setRunningMissingIds((existing) => {
      const next = new Set(existing);
      next.delete(rawProductId);
      return next;
    });

  const { mutate: runMissing } = useRunMissingRawProductPasses(
    (result) => {
      stopRunningMissing(result.rawProductId);
      const failed = result.steps.filter((step) => !step.success).map((step) => step.step);
      const ran = result.steps.filter((step) => step.success).map((step) => step.step);
      const parts = [
        ran.length ? `Read ${ran.join(', ')}` : 'Nothing was left to read',
        failed.length ? `failed: ${failed.join(', ')}` : '',
        result.skipped.length ? `waiting: ${result.skipped.join(', ')}` : '',
      ].filter(Boolean);
      snackbarService.showSnackbar(parts.join(' · '), failed.length ? 'info' : 'success');
      recordReadiness(result);
      refetch();
    },
    (_error, { rawProductId }) => {
      stopRunningMissing(rawProductId);
      snackbarService.showSnackbar('Could not read the missing sections', 'error');
    },
  );

  const handleRunMissing = (rawProductId: string, withContents: boolean) => {
    setRunningMissingIds((existing) => new Set(existing).add(rawProductId));
    runMissing({ rawProductId, withContents });
  };

  // Soft-deleting the suggestion takes its raw product down with it, so the row
  // leaves the queue on the next fetch.
  const { mutate: deleteSuggestion } = useDeleteProductSuggestion(
    () => {
      setDeletingSuggestionId(null);
      snackbarService.showSnackbar('Suggestion deleted', 'success');
      refetch();
    },
    () => {
      setDeletingSuggestionId(null);
      snackbarService.showSnackbar('Could not delete the suggestion', 'error');
    },
  );

  const handleDeleteSuggestion = (suggestionId: string) => {
    setDeletingSuggestionId(suggestionId);
    deleteSuggestion(suggestionId);
  };

  // The whole identity block comes back, for the same reason the pipeline's does:
  // a pass writes more than the field its control sits under.
  const { mutate: rerunStep } = useRerunRawProductStep(
    (result) => {
      setRerunningStep(null);
      setIdentities((existing) => ({ ...existing, [result.rawProductId]: result }));
      recordReadiness(result);
      snackbarService.showSnackbar(`Read ${result.step} off the pack again`, 'success');
    },
    () => {
      setRerunningStep(null);
      snackbarService.showSnackbar('Could not read that off the pack again', 'error');
    },
  );

  const handleRerunStep = (rawProductId: string, step: RawProductStep) => {
    setRerunningStep({ rawProductId, step });
    rerunStep({ rawProductId, step });
  };

  const { mutate: setAvatar } = useSetRawProductAvatar(
    (result) => {
      setSettingAvatarId(null);
      setAvatars((existing) => ({ ...existing, [result.rawProductId]: result.avatarMediaId }));
      recordReadiness(result);
      snackbarService.showSnackbar('Front photo set', 'success');
    },
    () => {
      setSettingAvatarId(null);
      snackbarService.showSnackbar('Could not set the front photo', 'error');
    },
  );

  const handleSetAvatar = (rawProductId: string, mediaId: string) => {
    setSettingAvatarId(rawProductId);
    setAvatar({ rawProductId, mediaId });
  };

  const { mutate: deleteImage } = useDeleteSuggestionImage(
    () => {
      snackbarService.showSnackbar('Image deleted', 'success');
      refetch();
    },
    () => snackbarService.showSnackbar('Could not delete the image', 'error'),
  );

  const total = data?.total ?? 0;
  const pageCount = Math.ceil(total / PAGE_SIZE);

  return (
    <Box className={globalStyles.container}>
      <Container maxWidth={'xl'} className={globalStyles.container}>
        <Box pt={2}>
          <PageHeader
            title={isSingle ? 'Raw Product' : 'Raw Product Review'}
            description={
              isSingle
                ? data?.items[0]?.barcode ?? undefined
                : total > 0
                  ? `${total} raw products not yet promoted`
                  : undefined
            }
          />
        </Box>

        {/* One pack, found by the number on it. A reviewer holding a product
            cannot page to it through thousands of rows, and the barcode is the
            only thing on a raw product that a person can read off the thing
            itself. */}
        {!isSingle && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={2}>
          <TextField
            size="small"
            value={barcodeInput}
            placeholder="Filter by barcode"
            onChange={(event) => setBarcodeInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') runSearch(barcodeInput);
            }}
            sx={{ width: 320, maxWidth: '100%' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: colors.gray }} />
                </InputAdornment>
              ),
              endAdornment: (barcodeInput || barcode) && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setBarcodeInput('');
                      runSearch('');
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 16, color: colors.gray }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* The queue narrowed to one leaf. Filling a category nobody has
              promoted into means reading what is already filed there, and there
              are hundreds of leaves - so it is typed at rather than scrolled. */}
          <Autocomplete
            size="small"
            options={filterCategories}
            value={leafCategory}
            onChange={(_event, next) => runCategoryFilter(next)}
            getOptionLabel={(option) => option.pathEN}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={{ width: 420, maxWidth: '100%' }}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Stack direction="row" alignItems="center" spacing={1} width="100%">
                  <CustomText text={option.pathEN} fontSize={13} />
                  {/* What is filed here and what is waiting, so a leaf nobody has
                      promoted into is picked out without opening it. */}
                  <Box flexGrow={1} />
                  <CustomText
                    text={`${option.products ?? 0} / ${option.waiting ?? 0}`}
                    fontSize={12}
                    color={option.products ? colors.gray : colors.warning}
                    noWrap
                    sx={{ flexShrink: 0 }}
                  />
                </Stack>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder="Filter by category (products / waiting)" />
            )}
          />
        </Stack>
        )}

        <Stack spacing={2.5} mt={3} pb={4}>
          {isLoading &&
            Array.from({ length: 3 }).map((_value, index) => (
              <Skeleton key={index} variant="rounded" height={320} />
            ))}

          {!isLoading && total === 0 && items.length === 0 && (
            <Box py={8} textAlign="center">
              <CustomText
                text={
                  isSingle
                    ? 'This raw product no longer exists'
                    : barcode
                      ? `No raw product matches "${barcode}"`
                      : leafCategory
                        ? `No raw product is filed under ${leafCategory.pathEN}`
                        : 'Nothing left to review'
                }
                fontSize={16}
                color={colors.gray}
              />
            </Box>
          )}

          {items.map((item) => (
            <RawProductReviewCard
              key={item.rawProductId}
              item={
                promotedProductIds[item.rawProductId]
                  ? { ...item, productId: promotedProductIds[item.rawProductId] }
                  : item
              }
              leafCategories={leafCategories ?? []}
              isConfirming={confirmingId === item.rawProductId}
              isPromoting={promotingId === item.rawProductId}
              isRunningMissing={runningMissingIds.has(item.rawProductId)}
              isReextracting={reextractingId === item.rawProductId}
              isDeletingSuggestion={deletingSuggestionId === item.suggestionId}
              isRedescribing={redescribingId === item.rawProductId}
              isSavingIdentity={savingIdentityId === item.rawProductId}
              isSavingInfo={savingInfoId === item.rawProductId}
              isReextractingInfo={reextractingInfoId === item.rawProductId}
              isReextractingBrand={reextractingBrandId === item.rawProductId}
              brandOptions={brandOptions ?? []}
              isEditingBrand={editingBrandId === item.rawProductId}
              isSavingBrand={savingBrandId === item.rawProductId}
              onEditBrand={setEditingBrandId}
              onCloseBrandEditor={() => setEditingBrandId(null)}
              onSaveBrand={handleSaveBrand}
              isReextractingContents={reextractingContentsId === item.rawProductId}
              isReextractingUsages={reextractingUsagesId === item.rawProductId}
              isSavingIngredients={savingIngredientsId === item.rawProductId}
              isReextractingWarnings={reextractingWarningsId === item.rawProductId}
              isRankingIngredients={rankingIngredientsId === item.rawProductId}
              isRerunningPipeline={rerunningPipelineId === item.rawProductId}
              editingField={
                editingField && editingField.rawProductId === item.rawProductId
                  ? editingField.field
                  : null
              }
              editingInfoField={
                editingInfoField && editingInfoField.rawProductId === item.rawProductId
                  ? editingInfoField.field
                  : null
              }
              isEditingCategory={editingCategoryId === item.rawProductId}
              identityValues={identityValues as ProductIdentityValues | undefined}
              countries={countries as CountryOption[] | undefined}
              identity={identities[item.rawProductId]}
              info={infos[item.rawProductId]}
              brand={brands[item.rawProductId]}
              description={descriptions[item.rawProductId]}
              contents={contents[item.rawProductId]}
              usages={usages[item.rawProductId]}
              ingredients={ingredients[item.rawProductId]}
              rankedIngredients={rankedIngredients[item.rawProductId]}
              warnings={warnings[item.rawProductId]}
              readiness={readiness[item.rawProductId]}
              avatarMediaId={avatars[item.rawProductId]}
              isSettingAvatar={settingAvatarId === item.rawProductId}
              rerunningStep={
                rerunningStep?.rawProductId === item.rawProductId ? rerunningStep.step : null
              }
              onConfirmCategory={handleConfirmCategory}
              onPromote={handlePromote}
              onRunMissing={handleRunMissing}
              onReextractOcr={handleReextractOcr}
              onRedescribe={handleRedescribe}
              onEditDescription={setEditingDescriptionId}
              isEditingDescription={editingDescriptionId === item.rawProductId}
              isSavingDescription={savingDescriptionId === item.rawProductId}
              onCloseDescriptionEditor={() => setEditingDescriptionId(null)}
              onSaveDescription={handleSaveDescription}
              isSavingUsages={savingUsagesId === item.rawProductId}
              onSaveUsages={handleSaveUsages}
              isSavingContents={savingContentsId === item.rawProductId}
              onSaveContents={handleSaveContents}
              isSavingWarnings={savingWarningsId === item.rawProductId}
              onSaveWarnings={handleSaveWarnings}
              attributes={attributes[item.rawProductId]}
              isReextractingAttributes={reextractingAttributesId === item.rawProductId}
              onReextractAttributes={handleReextractAttributes}
              isSavingAttributes={savingAttributesId === item.rawProductId}
              onSaveAttributes={handleSaveAttributes}
              onSetAvatar={handleSetAvatar}
              onRerunStep={handleRerunStep}
              onEditField={(rawProductId, field) => setEditingField({ rawProductId, field })}
              onCloseFieldEditor={() => setEditingField(null)}
              onSaveIdentity={handleSaveIdentity}
              onEditInfoField={(rawProductId, field) => setEditingInfoField({ rawProductId, field })}
              onCloseInfoFieldEditor={() => setEditingInfoField(null)}
              onSaveInfo={handleSaveInfo}
              onReextractInfo={handleReextractInfo}
              onReextractBrand={handleReextractBrand}
              onReextractContents={handleReextractContents}
              onReextractUsages={handleReextractUsages}
              onSaveIngredients={handleSaveIngredients}
              onReextractWarnings={handleReextractWarnings}
              onRankIngredients={handleRankIngredients}
              onRerunPipeline={handleRerunPipeline}
              onEditCategory={setEditingCategoryId}
              onCloseCategoryEditor={() => setEditingCategoryId(null)}
              onDeleteSuggestion={handleDeleteSuggestion}
              onDeleteImage={(suggestionId, mediaId) => deleteImage({ suggestionId, mediaId })}
            />
          ))}

          {pageCount > 1 && (
            <Box display="flex" alignItems="center" justifyContent="center" flexWrap="wrap" gap={1} pt={1}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_event, value) => setPage(value)}
                color="primary"
              />

              {/* The queue is worked through in order over hundreds of pages, so
                  where a reviewer left off is a place they come back to by number -
                  and it is almost never one of the few the pagination offers. Every
                  page as a button was the other way to answer that, and at this
                  length it is a wall of numbers to read rather than a control. */}
              <TextField
                size="small"
                type="number"
                value={pageInput}
                placeholder={`Page 1-${pageCount}`}
                onChange={(event) => setPageInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  const wanted = Number(pageInput);
                  // Out of range is a typo, not a request: the reviewer is told
                  // the range in the box and nothing is gained by silently
                  // rounding them to an end they did not ask for.
                  if (!Number.isInteger(wanted) || wanted < 1 || wanted > pageCount) return;
                  setPage(wanted);
                  setPageInput('');
                }}
                sx={{ width: 130, '& input': { textAlign: 'center' } }}
              />
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
